import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { genericCommentApi } from '../api/comments';
import { ApiError } from '../api/client';
import { useToast } from './Toast';

/**
 * Reusable comment thread for any HR entity - reuses the exact same
 * comments table already proven for Projects, now genuinely
 * project-independent (migration 023). Rolled out to Staff Profile
 * first as the representative case; extending this to every other
 * entity (contracts, vacancies, leave requests, etc.) is real,
 * straightforward follow-up work, not started yet.
 */
export function CommentsThread({ entityType, entityId }: { entityType: string; entityId: number }) {
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const commentsQuery = useQuery({ queryKey: ['comments', entityType, entityId], queryFn: () => genericCommentApi.list(entityType, entityId) });

  const createMutation = useMutation({
    mutationFn: (text: string) => genericCommentApi.create(entityType, entityId, text),
    onSuccess: () => { setBody(''); queryClient.invalidateQueries({ queryKey: ['comments', entityType, entityId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to post comment.'),
  });

  return (
    <section className="card">
      <h2>Comments</h2>
      {commentsQuery.data && (
        commentsQuery.data.data.length === 0 ? (
          <p className="muted">No comments yet.</p>
        ) : (
          <ul className="deadline-list">
            {commentsQuery.data.data.map((c) => (
              <li key={c.id}><strong>{c.author_name}</strong> <span className="muted">({new Date(c.created_at).toLocaleString()})</span><br />{c.body}</li>
            ))}
          </ul>
        )
      )}
      <div className="inline-form">
        <label style={{ flex: 1 }}>Add a comment<textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} /></label>
        <button onClick={() => { if (body.trim()) createMutation.mutate(body); else showError('Comment cannot be empty.'); }} disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Posting…' : 'Post'}
        </button>
      </div>
    </section>
  );
}
